import AppHeader from "./ds/AppHeader";
import { useCallback, useRef, useState } from "react";
import DsIcon from "./ds/Icon";
import IconButton from "./ds/IconButton";
import Menu, { MenuItem, MenuSeparator } from "./ds/Menu";
import { useTranslation } from "../context/TranslationContext";

/**
 * Shared chrome for the apps (Cloud, Courses, Gateway, Consulting, Live): Figma's
 * "App header" in the app's colours, optional actions, and a ⋯ properties menu.
 * Without `app` it's the compact title bar, for a screen inside an app.
 */
export default function MintSpaceAppWindow({
  isDark,
  app = null,
  icon: Icon,
  iconGradient = "from-sky-400 to-blue-600",
  title,
  subtitle,
  actions = null,
  menuItems = [],
  toolbar = null,
  children,
  className = "",
  bodyClassName = "",
  bodyOverflow = "auto",
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const overflowClass =
    bodyOverflow === "hidden"
      ? "overflow-hidden"
      : bodyOverflow === "visible"
        ? "overflow-visible"
        : "overflow-auto";

  const visibleMenuItems = menuItems.filter(Boolean);

  const controls = (
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {visibleMenuItems.length > 0 && (
            <span ref={menuRef}>
              <IconButton
                label={t("cloud.properties") || "Properties"}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <DsIcon name="more" size={18} />
              </IconButton>
              <Menu open={menuOpen} onClose={closeMenu} anchorRef={menuRef} label={t("cloud.properties") || "Properties"}>
                {visibleMenuItems.map((item) => {
                  if (item.type === "separator") return <MenuSeparator key={item.id || item.key} />;
                  const ItemIcon = item.icon;
                  return (
                    <MenuItem
                      key={item.id || item.label}
                      leading={ItemIcon ? <ItemIcon className="size-5 shrink-0" /> : null}
                      danger={item.danger}
                      disabled={item.disabled}
                      onClick={() => item.onClick?.()}
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
              </Menu>
            </span>
          )}
        </div>
  );

  return (
    <div className={`flex min-h-0 flex-1 flex-col gap-space-24 overflow-hidden ${className}`}>
      {app ? (
        <AppHeader app={app} eyebrow={title} title={title} subtitle={subtitle} end={controls} className="shrink-0" />
      ) : (
        <div className="flex shrink-0 items-center justify-between gap-space-12 rounded-radius-lg bg-bg-raised px-space-16 py-space-12">
          <div className="flex min-w-0 items-center gap-space-8">
            <div className={`flex size-8 shrink-0 items-center justify-center rounded-radius-sm bg-gradient-to-br ${iconGradient}`}>
              {Icon ? <Icon className="size-4 shrink-0 text-white" /> : null}
            </div>
            <div className="min-w-0">
              <p className="truncate text-subheadline-semibold text-label-primary">{title}</p>
              {subtitle ? <p className="truncate text-footnote text-label-secondary">{subtitle}</p> : null}
            </div>
          </div>
          {controls}
        </div>
      )}

      {toolbar}

      <div className={`relative min-h-0 flex-1 ${overflowClass} ${bodyClassName}`.trim()}>
        {children}
      </div>
    </div>
  );
}
